import { DEFAULT_PDF_PACK_KEY, resolvePdfOutputs } from './applicationFormPdfPacks';

/** Empty signed-doc shape used when a pack has no upload yet. */
export function emptySignedPackDoc() {
  return {
    status: 'NOT_REQUESTED',
    fileId: '',
    fileName: '',
    originalName: '',
    fileSize: 0,
    fileType: '',
    uploadedAt: null,
    uploadSource: '',
    requestNote: '',
    resubmitNote: '',
    rejectionReason: '',
    verifiedAt: null,
    uploadHistory: [],
  };
}

/** Restore fileId from uploadHistory when an older verify bug cleared the active file fields. */
function healSignedPackFileMeta(doc) {
  const signed = doc && typeof doc === 'object' ? { ...emptySignedPackDoc(), ...doc } : emptySignedPackDoc();
  if (String(signed.fileId || '').trim()) return signed;
  const history = Array.isArray(signed.uploadHistory) ? signed.uploadHistory : [];
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const id = String(history[i]?.fileId || '').trim();
    if (!id) continue;
    return {
      ...signed,
      fileId: id,
      fileName: String(signed.fileName || history[i].fileName || '').trim(),
      originalName: String(signed.originalName || history[i].originalName || '').trim(),
      fileSize: Number(signed.fileSize) || Number(history[i].fileSize) || 0,
      fileType: String(signed.fileType || history[i].fileType || '').trim(),
      uploadedAt: signed.uploadedAt || history[i].uploadedAt || null,
    };
  }
  return signed;
}

/**
 * Resolve signed document for one pack (mirrors server getSignedPack).
 * Base/default falls back to legacy signedApplicationDocument.
 */
export function getSignedDocForPack(submission, packKey = DEFAULT_PDF_PACK_KEY) {
  const key = String(packKey || DEFAULT_PDF_PACK_KEY).trim() || DEFAULT_PDF_PACK_KEY;
  const packs = Array.isArray(submission?.signedPdfPacks) ? submission.signedPdfPacks : [];
  const hit = packs.find((p) => String(p?.packKey || '').trim() === key);
  if (hit) return healSignedPackFileMeta(hit);
  if (key === DEFAULT_PDF_PACK_KEY) {
    return healSignedPackFileMeta(submission?.signedApplicationDocument || emptySignedPackDoc());
  }
  return emptySignedPackDoc();
}

function formSettingsFromSubmission(submission) {
  return submission?.applicationForm?.settings
    || submission?.form?.settings
    || submission?.formSettings
    || {};
}

/**
 * One row per PDF pack for signed upload / status UI.
 * @returns {{ packKey: string, title: string, isDefault: boolean, signed: object }[]}
 */
export function resolveSignedPackRows(submission) {
  const outputs = resolvePdfOutputs(formSettingsFromSubmission(submission));
  let packs = outputs.length
    ? outputs
    : [{ key: DEFAULT_PDF_PACK_KEY, title: 'Application form', isDefault: true, order: 0 }];

  // If form settings were not populated on the list payload, still surface uploaded packs.
  if (!outputs.length && Array.isArray(submission?.signedPdfPacks) && submission.signedPdfPacks.length) {
    const fromSigned = submission.signedPdfPacks
      .map((p, idx) => ({
        key: String(p?.packKey || '').trim(),
        title: String(p?.packKey || `Pack ${idx + 1}`).replace(/_/g, ' '),
        isDefault: String(p?.packKey || '') === DEFAULT_PDF_PACK_KEY,
        order: idx,
      }))
      .filter((p) => p.key);
    if (fromSigned.length) {
      const hasBase = fromSigned.some((p) => p.key === DEFAULT_PDF_PACK_KEY);
      packs = hasBase
        ? fromSigned
        : [{ key: DEFAULT_PDF_PACK_KEY, title: 'Application form', isDefault: true, order: 0 }, ...fromSigned];
    }
  }

  return packs.map((pack) => ({
    packKey: pack.key,
    title: pack.title || pack.key,
    isDefault: Boolean(pack.isDefault) || pack.key === DEFAULT_PDF_PACK_KEY,
    signed: getSignedDocForPack(submission, pack.key),
  }));
}

export function signedDocStatusOf(doc) {
  return String(doc?.status || 'NOT_REQUESTED').toUpperCase();
}

export function signedDocUploadSourceOf(doc) {
  return String(doc?.uploadSource || '').toUpperCase();
}

export function isStaffPendingSignedDoc(doc) {
  const st = signedDocStatusOf(doc);
  return st === 'PENDING_UPLOAD' || st === 'RESUBMIT_REQUESTED';
}

/**
 * Can voluntarily upload/replace for this pack.
 * Pass submission so voluntary upload is blocked once application status is SUBMITTED+.
 * `portalCanUpload` should use canPortalVoluntarilyUploadSignedPdf for voluntary path.
 */
export function canVoluntarilyUploadSignedPack(doc, {
  portalCanUpload = true,
  portalCanEdit,
  submission = null,
} = {}) {
  // Backward-compatible alias: older callers passed portalCanEdit.
  const allowed = portalCanUpload !== undefined && portalCanEdit === undefined
    ? portalCanUpload
    : (portalCanEdit !== undefined ? portalCanEdit : portalCanUpload);
  if (!allowed) return false;

  const appStatus = String(submission?.status || '').toUpperCase();
  if (['SUBMITTED', 'REVIEWED', 'ACCEPTED', 'WAITLISTED', 'REJECTED', 'ARCHIVED'].includes(appStatus)) {
    return false;
  }

  if (isStaffPendingSignedDoc(doc)) return false;
  const st = signedDocStatusOf(doc);
  const source = signedDocUploadSourceOf(doc);
  if (st === 'NOT_REQUESTED') return true;
  if (source === 'COORDINATOR_VOLUNTARY' && st === 'SUBMITTED') return true;
  return false;
}

export function signedPackSelectionKey(submissionId, packKey) {
  return `${String(submissionId)}::${String(packKey || DEFAULT_PDF_PACK_KEY)}`;
}
